import { useMemo } from 'react';

import { widgetConfig, currencies } from '../config/config';
import { Currency } from '../types/currency';
import { ChanceToMatch } from '../types/loanType';

// Sort currencies alphabetically for consistent model one-hot encoding
const MODEL_CURRENCY_ORDER = [...currencies].sort();

interface PredictionInput {
  amount: number;
  interestRate: number;
  period: number;
  currency: Currency;
}

interface FeatureStats {
  logAmount: { mean: number; std: number };
  interestRate: { mean: number; std: number };
  period: { mean: number; std: number };
}

function sigmoid(x: number): number {
  const clipped = Math.max(-500, Math.min(500, x));
  return 1 / (1 + Math.exp(-clipped));
}

function prepareFeatures(
  amount: number,
  interestRate: number,
  period: number,
  currencyCode: Currency,
  featureStats: FeatureStats
): number[] {
  const logAmount = Math.log(Math.max(1000, amount));
  const normalizedLogAmount =
    (logAmount - featureStats.logAmount.mean) / featureStats.logAmount.std;
  const normalizedInterestRate =
    (interestRate - featureStats.interestRate.mean) /
    featureStats.interestRate.std;
  const normalizedPeriod =
    (period - featureStats.period.mean) / featureStats.period.std;

  const currencyOneHotEncoding = MODEL_CURRENCY_ORDER.map(currency =>
    currency === currencyCode ? 1 : 0
  );

  return [
    normalizedLogAmount,
    normalizedInterestRate,
    normalizedPeriod,
    ...currencyOneHotEncoding,
  ];
}

function classifyProbability(
  probability: number,
  thresholds: number[]
): ChanceToMatch {
  if (probability < thresholds[0]) {
    return ChanceToMatch.LOW;
  }
  if (probability < thresholds[1]) {
    return ChanceToMatch.MEDIUM;
  }
  if (probability < thresholds[2]) {
    return ChanceToMatch.HIGH;
  }
  return ChanceToMatch.VERY_HIGH;
}

export function useChanceToMatch() {
  const computeChanceToMatch = useMemo(() => {
    const model = widgetConfig.matchingModel;
    if (!model) {
      return () => null;
    }
    const weights = model.parameters.params;
    const thresholds = model.likelihoodBandThresholds;

    // A degenerate (near-zero) logAmountStd means the model was fit on PLN-like
    // data where every amount was effectively identical. Normalizing by it
    // divides by ~0, saturating the sigmoid to 0 or 1 and collapsing the
    // optimal rate to either minRate or maxRate. Treat such an entry as
    // unusable and fall back to EUR's stats instead.
    const MIN_LOG_AMOUNT_STD = 1e-3;

    const getCurrencyStats = (currency: Currency): FeatureStats => {
      const { currencyStats, ...globalStats } = model.preprocessingStats;

      const isUsable = (entry: { logAmountStd: number } | undefined) =>
        entry !== undefined && entry.logAmountStd >= MIN_LOG_AMOUNT_STD;

      const currencyEntry = currencyStats.find(
        entry => entry.currency === currency
      );
      const fallbackEntry = currencyStats.find(
        entry => entry.currency === 'EUR'
      );
      const logAmountStats = isUsable(currencyEntry)
        ? currencyEntry
        : fallbackEntry;

      if (!logAmountStats) {
        throw new Error('No currency stats available');
      }

      return {
        logAmount: {
          mean: logAmountStats.logAmountMean,
          std: logAmountStats.logAmountStd,
        },
        interestRate: {
          mean: globalStats.interestRateMean,
          std: globalStats.interestRateStd,
        },
        period: {
          mean: globalStats.periodMean,
          std: globalStats.periodStd,
        },
      };
    };

    return (input: PredictionInput): ChanceToMatch | null => {
      try {
        if (
          !isFinite(input.amount) ||
          input.amount <= 0 ||
          !isFinite(input.interestRate) ||
          input.interestRate <= 0
        ) {
          return null;
        }

        const featureStats = getCurrencyStats(input.currency);
        const features = prepareFeatures(
          input.amount,
          input.interestRate,
          input.period,
          input.currency,
          featureStats
        );

        if (features.some(f => !isFinite(f))) {
          return ChanceToMatch.NA;
        }

        const augmentedFeatures = [1, ...features];
        const score = augmentedFeatures.reduce(
          (sum, feature, i) => sum + feature * weights[i],
          0
        );
        const probability = sigmoid(score);

        if (!isFinite(probability)) {
          return ChanceToMatch.NA;
        }

        return classifyProbability(probability, thresholds);
      } catch {
        return ChanceToMatch.NA;
      }
    };
  }, []);

  return { computeChanceToMatch };
}
